import { API } from '@/config';
import { AuthApiError } from '@/features/auth/api';

export type ProposalKind = 'new' | 'edit';
export type ProposalStatus = 'pending' | 'approved' | 'rejected';

export type CreateProposalRequest = {
  kind: ProposalKind;
  page_id?: string;
  base_revision?: string;
  proposed_markdown: string;
  summary?: string;
  title?: string;
  slug?: string;
  language?: string;
  translation_group?: string;
  categories?: string[];
};

export type ProposalResponse = {
  id: string;
  kind: ProposalKind;
  page_id?: string;
  base_revision?: string;
  proposed_markdown: string;
  status: ProposalStatus;
  author_id: string;
  moderator_id?: string;
  decided_at?: string;
  summary?: string;
  title?: string;
  slug?: string;
  language?: string;
  translation_group?: string;
  created_at: string;
  updated_at: string;
};

export type MediaUploadResponse = {
  key: string;
  url: string;
  page_id?: string;
};

type ApiEnvelope<T> = {
  ok?: boolean;
  data?: T;
  error?: unknown;
};

export type ProposeApiClient = {
  createProposal(input: CreateProposalRequest): Promise<ProposalResponse>;
  uploadMedia(input: { file: File; pageID?: string }): Promise<MediaUploadResponse>;
};

export function createProposeApiClient(
  getToken: () => string | null,
  baseURL = API.wikiBase,
): ProposeApiClient {
  const normalizedBaseURL = baseURL.replace(/\/+$/, '');

  return {
    createProposal(input) {
      const token = requireToken(getToken());

      return request<ProposalResponse>(`${normalizedBaseURL}/proposals`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(input),
      });
    },
    uploadMedia(input) {
      const token = requireToken(getToken());
      const body = new FormData();

      body.set('media', input.file);
      if (input.pageID) {
        body.set('page_id', input.pageID);
      }

      return request<MediaUploadResponse>(`${normalizedBaseURL}/moderation/media`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body,
      });
    },
  };
}

function requireToken(token: string | null): string {
  if (!token) {
    throw new AuthApiError('error_login_required', 401);
  }

  return token;
}

async function request<T>(input: string, init: RequestInit): Promise<T> {
  const response = await fetch(input, init);
  const body = await parseBody(response);

  if (!response.ok) {
    throw new AuthApiError(errorMessage(body, response.statusText), response.status);
  }

  if (isEnvelope<T>(body)) {
    if (body.ok === false) {
      throw new AuthApiError(errorMessage(body, response.statusText), response.status);
    }

    if ('data' in body) {
      return body.data as T;
    }
  }

  return body as T;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function isEnvelope<T>(body: unknown): body is ApiEnvelope<T> {
  return Boolean(body && typeof body === 'object' && 'ok' in body);
}

function errorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'error' in body) {
    const error = (body as { error?: unknown }).error;

    if (typeof error === 'string' && error.trim()) {
      return error;
    }

    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message?: unknown }).message;

      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
  }

  return fallback || 'request failed';
}
